'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  BodyStrong,
  IconButton,
  MetaDefault,
  Tooltip,
} from '@terra/ui-design-system';
import {
  PackageIcon,
  Brain01Icon,
  Database01Icon,
  Folder01Icon,
  MemoryStickIcon,
  Message01Icon,
  PanelLeftCloseIcon,
  Plug01Icon,
  Radar01Icon,
  SecurityCheckIcon,
  Wrench01Icon,
  type TerraIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  AI_SIGNALS_ASSET_KIND_NAV_BY_PROFILE,
  type AiSignalsAssetKind,
  type AiSignalsAssetKindProfile,
} from '../utils/aiSignalsAssetKind';

type AiSignalsAssetKindNavProps = {
  selectedKind: AiSignalsAssetKind;
  countsByKind: Record<AiSignalsAssetKind, number>;
  countsUnavailable?: boolean;
  profile: AiSignalsAssetKindProfile;
  onSelectKind: (kind: AiSignalsAssetKind) => void;
  onMinimize?: () => void;
  minimizeLabel?: string;
};
type KindLabelKey =
  | 'all'
  | 'appWide'
  | 'tools'
  | 'prompts'
  | 'resources'
  | 'tool'
  | 'model'
  | 'rag'
  | 'memory'
  | 'guardrail'
  | 'integration';
const KIND_ITEM_META: Record<
  AiSignalsAssetKind,
  {
    icon: TerraIcon;
    labelKey: KindLabelKey;
  }
> = {
  all: { icon: Radar01Icon, labelKey: 'all' },
  application: { icon: Folder01Icon, labelKey: 'appWide' },
  MCP_TOOL: { icon: Wrench01Icon, labelKey: 'tools' },
  MCP_PROMPT: { icon: Message01Icon, labelKey: 'prompts' },
  MCP_RESOURCE: { icon: PackageIcon, labelKey: 'resources' },
  TOOL: { icon: Wrench01Icon, labelKey: 'tool' },
  MODEL: { icon: Brain01Icon, labelKey: 'model' },
  RAG: { icon: Database01Icon, labelKey: 'rag' },
  MEMORY: { icon: MemoryStickIcon, labelKey: 'memory' },
  GUARDRAIL: { icon: SecurityCheckIcon, labelKey: 'guardrail' },
  INTEGRATION: { icon: Plug01Icon, labelKey: 'integration' },
};

export function AiSignalsAssetKindNav({
  selectedKind,
  countsByKind,
  countsUnavailable = false,
  profile,
  onSelectKind,
  onMinimize,
  minimizeLabel,
}: AiSignalsAssetKindNavProps) {
  const tDesignSystem = useTranslations('designSystem.applicationAssets');
  const tSignalDrawer = useTranslations('designSystem.signals.drawer');
  const tMethodFamilies = useTranslations(
    'aiApplications.overviewDashboard.methodFamilies',
  );
  const tComponentTypes = useTranslations('aiApplications.componentTypes');
  const kindItems = AI_SIGNALS_ASSET_KIND_NAV_BY_PROFILE[profile];
  const getLabel = (labelKey: KindLabelKey) => {
    if (labelKey === 'all') {
      return tDesignSystem('allAssets');
    }

    if (labelKey === 'appWide') {
      return tSignalDrawer('appWideSignal');
    }

    if (
      labelKey === 'tools' ||
      labelKey === 'prompts' ||
      labelKey === 'resources'
    ) {
      return tMethodFamilies(labelKey);
    }

    return tComponentTypes(labelKey);
  };

  return (
    <Box
      w="280px"
      minW="280px"
      borderRightWidth="1px"
      borderRightStyle="solid"
      borderRightColor="border.neutral.subtle"
      h="full"
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      {onMinimize && minimizeLabel ? (
        <HStack
          h="8"
          px="2"
          borderBottomWidth="1px"
          borderColor="border.subtle"
          bg="bg.canvas"
          justifyContent="flex-end"
          alignItems="center"
          flexShrink={0}
        >
          <Tooltip title={minimizeLabel} positioning={{ placement: 'bottom' }}>
            <IconButton
              variant="link"
              size="xs"
              onClick={onMinimize}
              aria-label={minimizeLabel}
              color="fg.muted"
              minW="auto"
              h="auto"
              p="1"
            >
              <PanelLeftCloseIcon size={12} />
            </IconButton>
          </Tooltip>
        </HStack>
      ) : null}
      <VStack gap={0} alignItems="stretch" flex="1" overflow="auto">
        {kindItems.map((kind) => {
          const isSelected = selectedKind === kind;
          const { icon: Icon, labelKey } = KIND_ITEM_META[kind];

          return (
            <HStack
              key={kind}
              px={4}
              py={2.5}
              w="full"
              cursor="pointer"
              bg={isSelected ? 'bg.neutral.subtle' : 'transparent'}
              _hover={{ bg: 'bg.muted' }}
              onClick={() => onSelectKind(kind)}
              justifyContent="space-between"
              alignItems="center"
            >
              <HStack gap={2} alignItems="center">
                <Icon size={14} />
                {isSelected ? (
                  <BodyStrong color="fg.default">
                    {getLabel(labelKey)}
                  </BodyStrong>
                ) : (
                  <BodyDefault color="fg.default">
                    {getLabel(labelKey)}
                  </BodyDefault>
                )}
              </HStack>
              <MetaDefault color="fg.muted">
                {countsUnavailable ? '—' : countsByKind[kind]}
              </MetaDefault>
            </HStack>
          );
        })}
      </VStack>
    </Box>
  );
}
